import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type EventApplicationModuleSpecsFragment } from '../../../../api/fragments/EventApplicationModuleSpecs';
import {
    UpdateEventApplicationModuleEmailContentsDocument,
    type UpdateEventApplicationModuleEmailContentsMutation,
    type UpdateEventApplicationModuleEmailContentsMutationVariables,
} from '../../../../api/mutations/updateEventApplicationModuleEmailContents';
import { EmailContentUpdateType } from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import EmailContentsForm from './EmailContentsForm';
import { type EventApplicationModuleEmailFormValues } from './type';
import useHandleEventApplicationModuleAssets from './useHandleEventApplicationModuleAssets';

type EventModuleEmailProps = {
    module: EventApplicationModuleSpecsFragment;
};

const validator = validators.compose(
    validators.requiredString('submitOrder.subject.defaultValue.defaultValue'),
    validators.requiredString('submitOrder.introTitle.defaultValue.defaultValue'),
    validators.requiredString('submitOrder.contentText.defaultValue.defaultValue')
);

const EmailContents = ({ module }: EventModuleEmailProps) => {
    const { t } = useTranslation(['configuratorModuleDetail', 'emails']);
    const apolloClient = useApolloClient();
    const validate = useValidator(validator);

    const handleEventModuleAssets = useHandleEventApplicationModuleAssets();

    const initialValues: EventApplicationModuleEmailFormValues = useMemo((): EventApplicationModuleEmailFormValues => {
        if (module.emailContents) {
            return module.emailContents;
        }

        return {
            submitOrder: {
                subject: {
                    defaultValue: {
                        defaultValue: t('emails:event.submitOrder.subject'),
                        overrides: [],
                    },
                    overrides: [],
                },
                introTitle: {
                    defaultValue: {
                        defaultValue: t('emails:event.submitOrder.introTitle'),
                        overrides: [],
                    },
                    overrides: [],
                },
                contentText: {
                    defaultValue: {
                        defaultValue: t('emails:event.submitOrder.contentText'),
                        overrides: [],
                    },
                    overrides: [],
                },
            },
        };
    }, [module.emailContents, t]);

    const onSubmit = useHandleError(
        async ({ submitOrder }: EventApplicationModuleEmailFormValues) => {
            const { introImage: submitOrderIntroImage, ...submitOrderSetting } = submitOrder;

            message.loading({
                content: t('configuratorModuleDetail:messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<
                    UpdateEventApplicationModuleEmailContentsMutation,
                    UpdateEventApplicationModuleEmailContentsMutationVariables
                >({
                    mutation: UpdateEventApplicationModuleEmailContentsDocument,
                    variables: {
                        moduleId: module.id,
                        settings: {
                            emailContentUpdateType: EmailContentUpdateType.Module,
                            submitOrder: submitOrderSetting,
                        },
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            await handleEventModuleAssets(
                module.id,
                { submitOrder },
                EmailContentUpdateType.Module,
                module.emailContents
            );

            await message.success({
                content: t('configuratorModuleDetail:messages.success'),
                key: 'primary',
            });
        },
        [apolloClient, handleEventModuleAssets, module.emailContents, module.id, t]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updateEmailForm"
                    name="updateEmailForm"
                    onSubmitCapture={handleSubmit}
                    style={{ marginTop: -16 }}
                >
                    <EmailContentsForm companyId={module.company.id} defaultValueDisabled={false} />
                </Form>
            )}
        </Formik>
    );
};

export default EmailContents;
